import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from "src/@/components/ui/button";
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Badge } from 'src/@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/@/components/ui/select";
import { Checkbox } from 'src/@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/@/components/ui/dialog";

import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'src/@/components/ui/tooltip';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { getListaUsuariosByClienteId, getListaSetoresTransferencia } from 'src/redux/slices/user';
import { getListaTags } from 'src/redux/slices/chat';
import { Plus, X, Trash2, Upload, FileText, Image, File, Download } from 'lucide-react';
import v2_actionCrmPesquisarLeads from 'src/actions/v2/crm/v2_actionCrmPesquisarLeads';
import actionPesquisarChamados from 'src/actions/chamados/actionPesquisarChamados';
import tarefasService from 'src/services/tarefasService';

const FILE_ICON_MAP = {
  'application/pdf': { icon: FileText, color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-500/10' },
  'image/': { icon: Image, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-500/10' },
  'default': { icon: File, color: 'text-gray-500', bg: 'bg-gray-50 dark:bg-gray-500/10' },
};

function getFileIcon(mimetype) {
  if (mimetype === 'application/pdf') return FILE_ICON_MAP['application/pdf'];
  if (mimetype?.startsWith('image/')) return FILE_ICON_MAP['image/'];
  return FILE_ICON_MAP['default'];
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function FieldLabel({ children, tooltip, required }) {
  return (
    <div className="flex items-center gap-1.5">
      <Label className="text-sm font-bold text-gray-900 dark:text-[#D1D2D3]">
        {children}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {tooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help text-gray-400 hover:text-gray-500 dark:text-[#ABABAD] dark:hover:text-[#D1D2D3] transition-colors">
              <Iconify icon="mdi:information-outline" width={14} />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[240px] text-xs">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

export default function TarefaDialog({ open, onClose, onSave, task, onDelete, workflows = [], selectedWorkflowId = '', chatId = null }) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { usuariosList, listaSetoresTransferencia } = useSelector((state) => state.user);
  const { listaTags } = useSelector((state) => state.chat);

  const [form, setForm] = useState({
    title: '',
    description: '',
    status: '',
    priority: 'medium',
    assigned_to: '',
    sector_id: '',
    due_date: '',
    workflow_id: '',
    tags: [],
    checklist: [],
    lead_id: null,
    chamado_id: null,
    chat_id: null,
    attachments: [],
  });
  const [saving, setSaving] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);

  // File upload
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Lead search
  const [leadSearch, setLeadSearch] = useState('');
  const [leadResults, setLeadResults] = useState([]);
  const [leadLoading, setLeadLoading] = useState(false);
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const leadDebounceRef = useRef(null);
  const leadDropdownRef = useRef(null);

  // Chamado search
  const [chamadoSearch, setChamadoSearch] = useState('');
  const [chamadoResults, setChamadoResults] = useState([]);
  const [chamadoLoading, setChamadoLoading] = useState(false);
  const [showChamadoDropdown, setShowChamadoDropdown] = useState(false);
  const [selectedChamado, setSelectedChamado] = useState(null);
  const chamadoDebounceRef = useRef(null);
  const chamadoDropdownRef = useRef(null);

  useEffect(() => {
    if (user?.clienteId) {
      if (usuariosList.length === 0) dispatch(getListaUsuariosByClienteId(user.clienteId));
      if (listaSetoresTransferencia.length === 0) dispatch(getListaSetoresTransferencia(user.clienteId));
      if (listaTags.length === 0) dispatch(getListaTags(`?clienteId=${user.clienteId}`));
    }
  }, [user?.clienteId]);

  const currentWorkflow = useMemo(() => {
    return workflows.find(w => w._id === form.workflow_id);
  }, [workflows, form.workflow_id]);

  const sortedEstagios = useMemo(() => {
    if (!currentWorkflow?.estagios) return [];
    return [...currentWorkflow.estagios].sort((a, b) => a.ordem - b.ordem);
  }, [currentWorkflow]);

  const filteredUsers = useMemo(() => {
    if (!form.sector_id) return usuariosList;
    return usuariosList.filter(u =>
      u.setores?.some(s => (s._id || s) === form.sector_id) || !u.setores?.length
    );
  }, [usuariosList, form.sector_id]);

  useEffect(() => {
    if (task) {
      const taskWorkflowId = task.workflow_id?._id || task.workflow_id || selectedWorkflowId;
      setForm({
        title: task.title || '',
        description: task.description || '',
        status: task.status || '',
        priority: task.priority || 'medium',
        assigned_to: task.assigned_to?._id || task.assigned_to || '',
        sector_id: task.sector_id?._id || task.sector_id || '',
        due_date: task.due_date ? task.due_date.slice(0, 10) : '',
        workflow_id: taskWorkflowId,
        tags: task.tags || [],
        checklist: task.checklist || [],
        lead_id: task.lead_id?._id || task.lead_id || null,
        chamado_id: task.chamado_id?._id || task.chamado_id || null,
        chat_id: task.chat_id?._id || task.chat_id || chatId || null,
        attachments: task.attachments || [],
      });
      if (task.lead_id && typeof task.lead_id === 'object') {
        setSelectedLead(task.lead_id);
      } else {
        setSelectedLead(null);
      }
      if (task.chamado_id && typeof task.chamado_id === 'object') {
        setSelectedChamado(task.chamado_id);
      } else {
        setSelectedChamado(null);
      }
    } else {
      const defaultWorkflowId = selectedWorkflowId;
      const defaultWorkflow = workflows.find(w => w._id === defaultWorkflowId);
      const firstStatus = defaultWorkflow?.estagios?.length > 0
        ? [...defaultWorkflow.estagios].sort((a, b) => a.ordem - b.ordem)[0].nome
        : 'A Fazer';
      setForm({
        title: '',
        description: '',
        status: firstStatus,
        priority: 'medium',
        assigned_to: '',
        sector_id: '',
        due_date: '',
        workflow_id: defaultWorkflowId,
        tags: [],
        checklist: [],
        lead_id: null,
        chamado_id: null,
        chat_id: chatId || null,
        attachments: [],
      });
      setSelectedLead(null);
      setSelectedChamado(null);
    }
    setPendingFiles([]);
    setUploading(false);
    setShowTagInput(false);
    setNewTag('');
    setNewChecklistItem('');
    setLeadSearch('');
    setLeadResults([]);
    setShowLeadDropdown(false);
    setChamadoSearch('');
    setChamadoResults([]);
    setShowChamadoDropdown(false);
  }, [task, open, selectedWorkflowId, workflows]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (leadDropdownRef.current && !leadDropdownRef.current.contains(e.target)) {
        setShowLeadDropdown(false);
      }
      if (chamadoDropdownRef.current && !chamadoDropdownRef.current.contains(e.target)) {
        setShowChamadoDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleWorkflowChange = (newWorkflowId) => {
    const wf = workflows.find(w => w._id === newWorkflowId);
    const firstStatus = wf?.estagios?.length > 0
      ? [...wf.estagios].sort((a, b) => a.ordem - b.ordem)[0].nome
      : '';
    setForm({ ...form, workflow_id: newWorkflowId, status: firstStatus });
  };

  // Lead search with debounce
  const searchLeads = useCallback((termo) => {
    if (leadDebounceRef.current) clearTimeout(leadDebounceRef.current);
    if (!termo || termo.length < 2) {
      setLeadResults([]);
      setShowLeadDropdown(false);
      return;
    }
    setLeadLoading(true);
    leadDebounceRef.current = setTimeout(async () => {
      try {
        const res = await v2_actionCrmPesquisarLeads(`?cliente_id=${user?.clienteId}&termo_pesquisa=${encodeURIComponent(termo)}&limite=10`);
        setLeadResults(res.data?.data || res.data || []);
        setShowLeadDropdown(true);
      } catch (err) {
        console.error('Erro ao pesquisar leads:', err);
        setLeadResults([]);
      } finally {
        setLeadLoading(false);
      }
    }, 300);
  }, [user?.clienteId]);

  const handleLeadSearchChange = (e) => {
    const value = e.target.value;
    setLeadSearch(value);
    searchLeads(value);
  };

  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    setForm({ ...form, lead_id: lead._id });
    setLeadSearch('');
    setLeadResults([]);
    setShowLeadDropdown(false);
  };

  const handleRemoveLead = () => {
    setSelectedLead(null);
    setForm({ ...form, lead_id: null });
  };

  // Chamado search with debounce
  const searchChamados = useCallback((termo) => {
    if (chamadoDebounceRef.current) clearTimeout(chamadoDebounceRef.current);
    if (!termo || termo.length < 2) {
      setChamadoResults([]);
      setShowChamadoDropdown(false);
      return;
    }
    setChamadoLoading(true);
    chamadoDebounceRef.current = setTimeout(async () => {
      try {
        const res = await actionPesquisarChamados({ cliente_id: user?.clienteId, search: termo, limit: 10 });
        setChamadoResults(res.data?.data || res.data || []);
        setShowChamadoDropdown(true);
      } catch (err) {
        console.error('Erro ao pesquisar chamados:', err);
        setChamadoResults([]);
      } finally {
        setChamadoLoading(false);
      }
    }, 300);
  }, [user?.clienteId]);

  const handleChamadoSearchChange = (e) => {
    const value = e.target.value;
    setChamadoSearch(value);
    searchChamados(value);
  };

  const handleSelectChamado = (chamado) => {
    setSelectedChamado(chamado);
    setForm({ ...form, chamado_id: chamado._id });
    setChamadoSearch('');
    setChamadoResults([]);
    setShowChamadoDropdown(false);
  };

  const handleRemoveChamado = () => {
    setSelectedChamado(null);
    setForm({ ...form, chamado_id: null });
  };

  // Tags
  const handleAddTag = (tagName) => {
    if (!tagName.trim()) return;
    if (form.tags.includes(tagName.trim())) return;
    setForm({ ...form, tags: [...form.tags, tagName.trim()] });
    setNewTag('');
    setShowTagInput(false);
  };

  const handleRemoveTag = (tagToRemove) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tagToRemove) });
  };

  // Checklist
  const handleAddChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    const newItem = {
      id: Date.now().toString(),
      text: newChecklistItem.trim(),
      checked: false,
    };
    setForm({ ...form, checklist: [...form.checklist, newItem] });
    setNewChecklistItem('');
  };

  const handleToggleChecklistItem = (itemId) => {
    setForm({
      ...form,
      checklist: form.checklist.map(item =>
        item.id === itemId ? { ...item, checked: !item.checked } : item
      ),
    });
  };

  const handleRemoveChecklistItem = (itemId) => {
    setForm({ ...form, checklist: form.checklist.filter(item => item.id !== itemId) });
  };

  const handleChecklistKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddChecklistItem();
    }
  };

  // File upload handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    processFiles(files);
  };

  const processFiles = async (files) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = files.filter(f => f.size <= maxSize);

    if (task) {
      // Edit mode: upload immediately
      setUploading(true);
      for (const file of validFiles) {
        try {
          const response = await tarefasService.uploadArquivo(task._id, file);
          if (!response.error && response.data) {
            setForm(prev => ({ ...prev, attachments: [...prev.attachments, response.data] }));
          }
        } catch (err) {
          console.error('Erro ao enviar arquivo:', err);
        }
      }
      setUploading(false);
    } else {
      // Create mode: queue files
      setPendingFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleRemoveAttachment = async (attachment) => {
    if (task && attachment._id) {
      try {
        await tarefasService.removerArquivo(task._id, attachment._id);
        setForm(prev => ({
          ...prev,
          attachments: prev.attachments.filter(a => a._id !== attachment._id)
        }));
      } catch (err) {
        console.error('Erro ao remover arquivo:', err);
      }
    }
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.status || !form.priority) return;
    setSaving(true);
    try {
      const data = { ...form };
      delete data.attachments; // Don't send attachments in the form data
      if (!data.assigned_to) delete data.assigned_to;
      if (!data.sector_id) delete data.sector_id;
      if (!data.due_date) delete data.due_date;
      if (!data.lead_id) data.lead_id = null;
      if (!data.chamado_id) data.chamado_id = null;
      if (!data.chat_id) delete data.chat_id;
      data.userId = user?.id;
      const savedTask = await onSave(data);

      // Upload pending files for new tasks
      if (!task && savedTask?._id && pendingFiles.length > 0) {
        setUploading(true);
        for (const file of pendingFiles) {
          try {
            await tarefasService.uploadArquivo(savedTask._id, file);
          } catch (err) {
            console.error('Erro ao enviar arquivo pendente:', err);
          }
        }
        setPendingFiles([]);
        setUploading(false);
      }

      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (onDelete && task) {
      onDelete(task);
      onClose();
    }
  };

  const checklistProgress = form.checklist.length > 0
    ? Math.round((form.checklist.filter(i => i.checked).length / form.checklist.length) * 100)
    : 0;

  const isOverdue = useMemo(() => {
    if (!form.due_date) return false;
    if (form.status === 'Concluído' || form.status === 'done') return false;
    if (currentWorkflow?.estagios?.length > 0) {
      const sorted = [...currentWorkflow.estagios].sort((a, b) => a.ordem - b.ordem);
      if (sorted[sorted.length - 1]?.nome === form.status) return false;
    }
    const due = form.due_date.slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);
    return due < today;
  }, [form.due_date, form.status, currentWorkflow]);

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const allAttachments = [...form.attachments, ...pendingFiles.map((f, i) => ({ _pending: true, _index: i, original_name: f.name, mimetype: f.type, size: f.size }))];

  // Guard: don't render creation form if no workflows exist and not editing
  if (open && workflows.length === 0 && !task) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[90vw] w-full md:w-[1200px] h-[90vh] p-0 flex flex-col rounded-2xl overflow-hidden border border-gray-200/50 dark:border-gray-700/50 bg-white dark:bg-[#222529] shadow-2xl [&>button]:hidden">
        <TooltipProvider delayDuration={200}>
        <DialogHeader className="sr-only">
          <DialogTitle>{task ? 'Editar Tarefa' : 'Nova Tarefa'}</DialogTitle>
        </DialogHeader>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-[#383838] flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${task ? 'bg-green-50 dark:bg-green-500/10' : 'bg-violet-50 dark:bg-violet-500/10'}`}>
              <Iconify
                icon={task ? "eva:edit-2-outline" : "eva:plus-outline"}
                className={task ? "text-green-600 dark:text-green-400" : "text-[#7C3AED]"}
                width={20}
              />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-[#D1D2D3]">
                {task ? 'Editar Tarefa' : 'Nova Tarefa'}
              </h2>
              <p className="text-xs text-gray-500 dark:text-[#ABABAD]">
                {task ? 'Atualize as informações da tarefa' : 'Preencha os campos para criar uma nova tarefa'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-[#383838] transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-[#D1D2D3]"
          >
            <X size={20} />
          </button>
        </div>

        {/* Overdue Banner */}
        {isOverdue && (
          <div className="px-6 py-2.5 bg-red-50 dark:bg-red-500/10 border-b border-red-100 dark:border-red-500/20 flex items-center gap-2 flex-shrink-0">
            <Iconify icon="eva:clock-outline" width={16} className="text-red-500 flex-shrink-0" />
            <span className="text-xs font-medium text-red-600 dark:text-red-400">
              Tarefa atrasada — prazo era {form.due_date.split('-').reverse().join('/')}
            </span>
          </div>
        )}

        {/* Content - Two Columns */}
        <div className="flex-1 overflow-hidden flex flex-row min-h-0">
          {/* Left Column - Form */}
          <div className="w-[60%] overflow-y-auto border-r border-gray-100 dark:border-[#383838] px-6 py-5 space-y-5">

            {/* Título */}
            <div>
              <FieldLabel required tooltip="Nome curto e descritivo para identificar a tarefa na listagem e no kanban.">Título</FieldLabel>
              <Input
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="Título da tarefa"
                className="mt-1.5 border-gray-200 dark:border-[#383838] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 rounded-xl dark:bg-[#1A1D21] dark:text-[#D1D2D3]"
              />
            </div>

            {/* Descrição */}
            <div>
              <FieldLabel tooltip="Detalhes adicionais sobre o que precisa ser feito nesta tarefa.">Descrição</FieldLabel>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Descrição da tarefa"
                rows={3}
                className="mt-1.5 w-full px-3 py-2 text-sm border border-gray-200 dark:border-[#383838] rounded-xl focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED]/20 focus:outline-none resize-none dark:bg-[#1A1D21] dark:text-[#D1D2D3]"
              />
            </div>

            {/* Workflow + Status side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Workflow */}
              {workflows.length > 0 && (
                <div>
                  <FieldLabel required tooltip="Fluxo de trabalho que define os estágios pelos quais esta tarefa irá passar.">Workflow</FieldLabel>
                  <Select value={form.workflow_id || undefined} onValueChange={handleWorkflowChange}>
                    <SelectTrigger className="mt-1.5 rounded-xl border-gray-200 dark:border-[#383838] dark:bg-[#1A1D21] dark:text-[#D1D2D3]">
                      <SelectValue placeholder="Selecionar..." />
                    </SelectTrigger>
                    <SelectContent className="z-[10000]">
                      {workflows.map(w => (
                        <SelectItem key={w._id} value={w._id}>{w.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Status */}
              <div>
                <FieldLabel required tooltip="Estágio atual da tarefa dentro do workflow selecionado.">Status</FieldLabel>
                <Select value={form.status || undefined} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1.5 rounded-xl border-gray-200 dark:border-[#383838] dark:bg-[#1A1D21] dark:text-[#D1D2D3]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[10000]">
                    {sortedEstagios.length > 0 ? (
                      sortedEstagios.map(e => (
                        <SelectItem key={e.nome} value={e.nome}>
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.cor }} />
                            {e.nome}
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="A Fazer">A Fazer</SelectItem>
                        <SelectItem value="Em Progresso">Em Progresso</SelectItem>
                        <SelectItem value="Concluído">Concluído</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Setor + Responsável side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel tooltip="Setor responsável pela tarefa. Ao selecionar, filtra os responsáveis disponíveis.">Setor</FieldLabel>
                <Select value={form.sector_id || "none"} onValueChange={v => setForm({ ...form, sector_id: v === "none" ? "" : v, assigned_to: '' })}>
                  <SelectTrigger className="mt-1.5 rounded-xl border-gray-200 dark:border-[#383838] dark:bg-[#1A1D21] dark:text-[#D1D2D3]">
                    <SelectValue placeholder="Todos os setores" />
                  </SelectTrigger>
                  <SelectContent className="z-[10000]">
                    <SelectItem value="none">Todos os setores</SelectItem>
                    {listaSetoresTransferencia.map(s => (
                      <SelectItem key={s._id} value={s._id}>{s.nome_setor}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <FieldLabel tooltip="Usuário que será responsável por executar esta tarefa.">Responsável</FieldLabel>
                <Select value={form.assigned_to || "none"} onValueChange={v => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5 rounded-xl border-gray-200 dark:border-[#383838] dark:bg-[#1A1D21] dark:text-[#D1D2D3]">
                    <SelectValue placeholder="Selecionar..." />
                  </SelectTrigger>
                  <SelectContent className="z-[10000]">
                    <SelectItem value="none">Nenhum</SelectItem>
                    {filteredUsers.map(u => (
                      <SelectItem key={u._id} value={u._id}>{u.nomeFantasia || u.nome || u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Prioridade + Data side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel required tooltip="Nível de urgência: Baixa, Média, Alta ou Urgente. Tarefas urgentes aparecem em destaque.">Prioridade</FieldLabel>
                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1.5 rounded-xl border-gray-200 dark:border-[#383838] dark:bg-[#1A1D21] dark:text-[#D1D2D3]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[10000]">
                    <SelectItem value="low">Baixa</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <FieldLabel tooltip="Prazo limite para conclusão. Tarefas vencidas serão marcadas como atrasadas.">Data de conclusão</FieldLabel>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={e => setForm({ ...form, due_date: e.target.value })}
                  className="mt-1.5 border-gray-200 dark:border-[#383838] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 rounded-xl dark:bg-[#1A1D21] dark:text-[#D1D2D3]"
                />
              </div>
            </div>

            {/* Lead / Negócio + Chamado side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel tooltip="Vincule esta tarefa a um lead ou negócio do CRM. Pesquise pelo nome do lead.">Lead / Negócio</FieldLabel>
                {selectedLead ? (
                  <div className="mt-1.5 flex items-center gap-2 p-2.5 bg-violet-50/50 dark:bg-violet-500/5 border border-violet-100 dark:border-violet-500/20 rounded-xl">
                    <div className="w-7 h-7 rounded-lg bg-violet-100 dark:bg-violet-500/15 flex items-center justify-center flex-shrink-0">
                      <Iconify icon="mdi:handshake-outline" width={14} className="text-[#7C3AED]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-[#D1D2D3] truncate">{selectedLead.nome}</p>
                      {selectedLead.valor !== undefined && selectedLead.valor !== null && (
                        <p className="text-xs text-gray-500 dark:text-[#ABABAD]">{formatCurrency(selectedLead.valor)}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveLead}
                      className="p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="relative mt-1.5" ref={leadDropdownRef}>
                    <div className="relative">
                      <Input
                        value={leadSearch}
                        onChange={handleLeadSearchChange}
                        placeholder="Pesquisar lead..."
                        className="pl-9 border-gray-200 dark:border-[#383838] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 rounded-xl dark:bg-[#1A1D21] dark:text-[#D1D2D3]"
                      />
                      <Iconify
                        icon={leadLoading ? "mdi:loading" : "eva:search-outline"}
                        width={16}
                        className={`absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 ${leadLoading ? 'animate-spin' : ''}`}
                      />
                    </div>
                    {showLeadDropdown && leadResults.length > 0 && (
                      <div className="absolute z-[10001] w-full mt-1 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] rounded-xl shadow-lg max-h-48 overflow-y-auto">
                        {leadResults.map(lead => (
                          <button
                            key={lead._id}
                            type="button"
                            onClick={() => handleSelectLead(lead)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-[#383838]/50 transition-colors text-left"
                          >
                            <div className="w-7 h-7 rounded-lg bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                              <Iconify icon="mdi:handshake-outline" width={14} className="text-[#7C3AED]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-[#D1D2D3] truncate">{lead.nome}</p>
                              {lead.valor !== undefined && lead.valor !== null && (
                                <p className="text-xs text-gray-500 dark:text-[#ABABAD]">{formatCurrency(lead.valor)}</p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {showLeadDropdown && leadResults.length === 0 && !leadLoading && leadSearch.length >= 2 && (
                      <div className="absolute z-[10001] w-full mt-1 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] rounded-xl shadow-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-[#ABABAD] text-center">Nenhum lead encontrado</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <FieldLabel tooltip="Vincule esta tarefa a um chamado existente. Pesquise pelo título ou número do chamado.">Chamado</FieldLabel>
                {selectedChamado ? (
                  <div className="mt-1.5 flex items-center gap-2 p-2.5 bg-blue-50/50 dark:bg-blue-500/5 border border-blue-100 dark:border-blue-500/20 rounded-xl">
                    <div className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                      <Iconify icon="mdi:ticket-outline" width={14} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-[#D1D2D3] truncate">
                        {selectedChamado.chamado ? `#${selectedChamado.chamado} - ` : ''}{selectedChamado.titulo}
                      </p>
                      {selectedChamado.status && (
                        <p className="text-xs text-gray-500 dark:text-[#ABABAD]">Status: {selectedChamado.status}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveChamado}
                      className="p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="relative mt-1.5" ref={chamadoDropdownRef}>
                    <div className="relative">
                      <Input
                        value={chamadoSearch}
                        onChange={handleChamadoSearchChange}
                        placeholder="Pesquisar chamado..."
                        className="pl-9 border-gray-200 dark:border-[#383838] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 rounded-xl dark:bg-[#1A1D21] dark:text-[#D1D2D3]"
                      />
                      <Iconify
                        icon={chamadoLoading ? "mdi:loading" : "eva:search-outline"}
                        width={16}
                        className={`absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 ${chamadoLoading ? 'animate-spin' : ''}`}
                      />
                    </div>
                    {showChamadoDropdown && chamadoResults.length > 0 && (
                      <div className="absolute z-[10001] w-full mt-1 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] rounded-xl shadow-lg max-h-48 overflow-y-auto">
                        {chamadoResults.map(chamado => (
                          <button
                            key={chamado._id}
                            type="button"
                            onClick={() => handleSelectChamado(chamado)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-[#383838]/50 transition-colors text-left"
                          >
                            <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                              <Iconify icon="mdi:ticket-outline" width={14} className="text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-[#D1D2D3] truncate">
                                {chamado.chamado ? `#${chamado.chamado} - ` : ''}{chamado.titulo}
                              </p>
                              {chamado.status && (
                                <p className="text-xs text-gray-500 dark:text-[#ABABAD]">{chamado.status}</p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {showChamadoDropdown && chamadoResults.length === 0 && !chamadoLoading && chamadoSearch.length >= 2 && (
                      <div className="absolute z-[10001] w-full mt-1 bg-white dark:bg-[#222529] border border-gray-200 dark:border-[#383838] rounded-xl shadow-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-[#ABABAD] text-center">Nenhum chamado encontrado</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Right Column - Metadata, Checklist, Tags, Attachments */}
          <div className="w-[40%] overflow-y-auto bg-gray-50/50 dark:bg-[#1A1D21]/30 px-6 py-5 space-y-5">

            {/* Metadados */}
            {task && (
              <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-4 space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Informações</h3>
                {task.created_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-[#ABABAD]">Criado em</span>
                    <span className="text-xs font-medium text-gray-700 dark:text-[#D1D2D3]">
                      {new Date(task.created_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                )}
                {task.updated_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-[#ABABAD]">Atualizado em</span>
                    <span className="text-xs font-medium text-gray-700 dark:text-[#D1D2D3]">
                      {new Date(task.updated_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                )}
                {task.created_by && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-[#ABABAD]">Criado por</span>
                    <span className="text-xs font-medium text-gray-700 dark:text-[#D1D2D3]">
                      {task.created_by?.nomeFantasia || task.created_by?.nome || task.created_by?.email || '-'}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Checklist */}
            <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD]">Checklist</h3>
                {form.checklist.length > 0 && (
                  <span className="text-[10px] font-medium text-gray-400 dark:text-[#ABABAD]">
                    {form.checklist.filter(i => i.checked).length}/{form.checklist.length}
                  </span>
                )}
              </div>

              {form.checklist.length > 0 && (
                <div className="w-full h-1.5 bg-gray-100 dark:bg-[#383838] rounded-full mb-3 overflow-hidden">
                  <div
                    className="h-full bg-[#7C3AED] rounded-full transition-all duration-300"
                    style={{ width: `${checklistProgress}%` }}
                  />
                </div>
              )}

              <div className="space-y-1">
                {form.checklist.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-2 group p-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-[#1A1D21] transition-colors"
                  >
                    <Checkbox
                      checked={item.checked}
                      onCheckedChange={() => handleToggleChecklistItem(item.id)}
                      className="data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED]"
                    />
                    <span className={`flex-1 text-sm ${item.checked ? 'line-through text-gray-400 dark:text-[#ABABAD]/50' : 'text-gray-700 dark:text-[#D1D2D3]'}`}>
                      {item.text}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveChecklistItem(item.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-400 hover:text-red-500 transition-all"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 mt-2">
                <Input
                  value={newChecklistItem}
                  onChange={e => setNewChecklistItem(e.target.value)}
                  onKeyDown={handleChecklistKeyDown}
                  placeholder="Adicionar item..."
                  className="flex-1 h-8 text-sm border-gray-200 dark:border-[#383838] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 rounded-lg dark:bg-[#1A1D21] dark:text-[#D1D2D3]"
                />
                <button
                  type="button"
                  onClick={handleAddChecklistItem}
                  disabled={!newChecklistItem.trim()}
                  className="flex items-center gap-1 text-xs text-[#7C3AED] font-medium hover:underline disabled:opacity-40 disabled:no-underline"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>

            {/* Tags */}
            <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD] mb-3">Tags</h3>
              <div className="flex flex-wrap items-center gap-1.5 min-h-[32px]">
                {form.tags.map((tag, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="text-xs font-medium flex items-center gap-1 bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/30 pr-1"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-0.5 p-0.5 rounded-full hover:bg-violet-200 dark:hover:bg-violet-500/20 transition-colors"
                    >
                      <X size={10} />
                    </button>
                  </Badge>
                ))}

                {showTagInput ? (
                  <div className="flex items-center gap-1">
                    <Input
                      value={newTag}
                      onChange={e => setNewTag(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); handleAddTag(newTag); }
                        if (e.key === 'Escape') { setShowTagInput(false); setNewTag(''); }
                      }}
                      placeholder="Nome da tag"
                      className="h-6 w-24 text-xs border-gray-200 dark:border-[#383838] rounded-md px-1.5 dark:bg-[#1A1D21] dark:text-[#D1D2D3]"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => handleAddTag(newTag)}
                      className="text-[#7C3AED] hover:text-[#6d28d9]"
                    >
                      <Iconify icon="eva:checkmark-outline" width={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowTagInput(true)}
                    className="flex items-center gap-0.5 text-xs text-gray-400 hover:text-[#7C3AED] transition-colors"
                  >
                    <Plus size={12} />
                    <span>Adicionar</span>
                  </button>
                )}
              </div>

              {showTagInput && listaTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {listaTags
                    .filter(t => !form.tags.includes(t.nome) && t.nome?.toLowerCase().includes(newTag.toLowerCase()))
                    .slice(0, 8)
                    .map(t => (
                      <button
                        key={t._id}
                        type="button"
                        onClick={() => handleAddTag(t.nome)}
                        className="text-[10px] px-2 py-0.5 rounded-full border border-gray-200 dark:border-[#383838] text-gray-500 dark:text-[#ABABAD] hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors"
                      >
                        {t.nome}
                      </button>
                    ))
                  }
                </div>
              )}
            </div>

            {/* Anexos - Upload */}
            <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD] mb-3">Anexos</h3>
              <div
                className="border-2 border-dashed border-gray-200 dark:border-[#383838] rounded-xl p-4 text-center hover:border-[#7C3AED]/40 dark:hover:border-[#7C3AED]/40 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDrop={handleFileDrop}
              >
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
                {uploading ? (
                  <Iconify icon="mdi:loading" width={24} className="text-[#7C3AED] mx-auto animate-spin" />
                ) : (
                  <Upload size={24} className="text-gray-400 dark:text-[#ABABAD] mx-auto" />
                )}
                <p className="text-xs text-gray-500 dark:text-[#ABABAD] mt-1">Arraste arquivos ou clique para selecionar</p>
                <p className="text-[10px] text-gray-400 dark:text-[#ABABAD]/70">PDF, imagens, documentos (max 10MB)</p>
              </div>
            </div>

            {/* Anexos list */}
            {allAttachments.length > 0 && (
              <div className="bg-white dark:bg-[#222529] rounded-xl border border-gray-100 dark:border-[#383838] p-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-[#ABABAD] mb-3">
                  Arquivos ({allAttachments.length})
                </h3>
                <div className="space-y-2">
                  {allAttachments.map((attachment, idx) => {
                    const fileInfo = getFileIcon(attachment.mimetype);
                    const IconComp = fileInfo.icon;
                    const isPending = attachment._pending;

                    return (
                      <div
                        key={attachment._id || `pending-${idx}`}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${isPending ? 'border-dashed border-amber-200 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-500/5' : 'border-gray-100 dark:border-[#383838] hover:bg-gray-50 dark:hover:bg-[#1A1D21]'}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${fileInfo.bg}`}>
                          <IconComp size={16} className={fileInfo.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          {!isPending && attachment.url ? (
                            <a
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-gray-900 dark:text-[#D1D2D3] truncate block hover:text-[#7C3AED] transition-colors"
                            >
                              {attachment.original_name || attachment.filename}
                            </a>
                          ) : (
                            <p className="text-sm font-medium text-gray-900 dark:text-[#D1D2D3] truncate">
                              {attachment.original_name || attachment.filename}
                            </p>
                          )}
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-400 dark:text-[#ABABAD]">
                              {formatFileSize(attachment.size)}
                            </span>
                            {isPending && (
                              <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30">
                                Pendente
                              </Badge>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => isPending ? handleRemovePendingFile(attachment._index) : handleRemoveAttachment(attachment)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex-shrink-0"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 dark:border-[#383838] bg-gray-50/80 dark:bg-[#1A1D21]/50 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            {task && onDelete && (
              <button
                type="button"
                onClick={handleDeleteClick}
                className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-700 font-medium transition-colors"
              >
                <Trash2 size={14} />
                Excluir
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="rounded-xl border-gray-200 dark:border-[#383838] dark:text-[#D1D2D3]"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || uploading || !form.title.trim() || !form.status || !form.priority}
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl shadow-sm shadow-[#7C3AED]/20"
            >
              {(saving || uploading) && <Iconify icon="mdi:loading" className="mr-2 animate-spin" width={16} />}
              {task ? 'Salvar' : 'Criar Tarefa'}
            </Button>
          </div>
        </div>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}
